import "./financeTable.scss";
import { DataGrid } from "@mui/x-data-grid";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useSelector } from "react-redux";

const FinanceTable = () => {
  let userId = useSelector((state) => state.user.userId);
  const adminType = useSelector((state) => state.user.adminType);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFinance, setSelectedFinance] = useState(null);

  if (adminType === "sAdmin") {
    userId = "1";
  }

  // Fetch all Finance
  useEffect(() => {
    const fetchFinance = async () => {
      if (!BASE_URL) {
        setError("BASE_URL is not defined.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null); // Reset error before fetching
        const res = await axios.get(
          `${BASE_URL}/api/finance/findAllFinance/${userId}`
        );
        setData(res.data);
      } catch (err) {
        setError("Failed to fetch Finance data.");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchFinance();
    }
  }, [userId, BASE_URL]);

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/api/finance/deleteFinance/${id}`
      );
      if (response.status === 200) {
        setData((prevData) => prevData.filter((item) => item._id !== id));
      }
    } catch (err) {
      console.error("Error deleting Finance:", err);
    }
  };

  const handleUpdate = (financeData) => {
    navigate("/finance/newFinance", { state: { finance: financeData } }); // Pass data to update screen
  };

  const financeListColumns = [
    { field: "_id", headerName: "ID", width: 70 },
    { field: "title", headerName: "Title", width: 400 },
    { field: "createdDate", headerName: "Created Date", width: 110 },
    { field: "amount", headerName: "Amount", width: 100 },
    { field: "type", headerName: "Type", width: 90 },
    { field: "editedDate", headerName: "Edited Date", width: 110 },
  ];

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 200,
      renderCell: (params) => (
        <div className="cellAction">
          <Tooltip title="Add Finance">
            <IconButton color="primary" aria-label="addButton">
              <Link to="/finance/newFinance">
                <Add />
              </Link>
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit Finance">
            <IconButton
              onClick={() => handleUpdate(params.row)}
              color="primary"
              aria-label="update"
            >
              <Edit />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete Finance">
            <IconButton
              onClick={() => {
                setSelectedFinance(params.row);
                setOpenDialog(true);
              }}
              color="error"
              aria-label="delete"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedFinance(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedFinance) {
      await handleDelete(selectedFinance._id);
      setOpenDialog(false);
    }
  };

  return (
    <div className="datatable">
      <div className="datatableTitle">
        <Typography variant="h5" gutterBottom>
          List Of Finance
        </Typography>
        <Link to="/finance/newFinance">
          <Button
            variant="contained"
            color="primary"
            style={{ marginRight: 25 }}
          >
            Add Finance
          </Button>
        </Link>
      </div>

      <Grid container justifyContent="center">
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "20px 0",
            }}
          >
            <CircularProgress />
          </div>
        ) : error ? (
          <Typography color="error" align="center">
            {error}
          </Typography>
        ) : (
          <DataGrid
            className="datagrid"
            rows={data}
            columns={financeListColumns.concat(actionColumn)}
            pageSize={5}
            rowsPerPageOptions={[5]}
            checkboxSelection
            getRowId={(row) => row._id}
          />
        )}
      </Grid>

      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this Finance?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FinanceTable;
